import { useEffect, useRef, useState } from "react";
import { C } from "@/lib/brand";

// Renders a block-based content document with a tab switcher across the top.
//
// tabs: [{ key, label, blocks: [{ type, ... }] }]
// block types supported: "richtext" | "image" | "video" | "callout" | "steps"
//
// Designed to be resilient — unknown block types are skipped silently.

function RichTextBlock({ block }) {
  // Prefer explicit html field, but also detect HTML inside `text` (CMS often
  // ships rich text with tags in the text field). If it looks like HTML,
  // render as HTML; otherwise render as plain text with whitespace preserved.
  const html = block.html;
  const text = block.text || "";
  const looksLikeHtml = /<\/?[a-z][\s\S]*?>/i.test(text);

  if (html || looksLikeHtml) {
    return (
      <div
        className="voya9-richtext"
        style={{ fontSize: 15, lineHeight: 1.7, color: C.ink }}
        dangerouslySetInnerHTML={{ __html: html || text }}
      />
    );
  }
  return (
    <p style={{ fontSize: 15, lineHeight: 1.7, color: C.ink, whiteSpace: "pre-wrap", margin: 0 }}>
      {text}
    </p>
  );
}

function ImageBlock({ block }) {
  if (!block.src && !block.url) return null;
  const src = block.src || block.url;
  return (
    <figure style={{ margin: 0 }}>
      <img src={src} alt={block.alt || ""} style={{ display: "block", width: "100%", height: "auto" }} />
      {block.caption && (
        <figcaption style={{ marginTop: 10, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 14, color: C.warmGray }}>
          {block.caption}
        </figcaption>
      )}
    </figure>
  );
}

function VideoBlock({ block }) {
  const src = block.src || block.url || block.embedUrl;
  if (!src) return null;
  // Treat as YouTube-like embed if it's an embed URL, otherwise HTML5 video.
  const isEmbed = /youtube|vimeo|player/.test(src);
  return (
    <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, background: C.warmBlack }}>
      {isEmbed ? (
        <iframe
          src={src}
          title={block.title || "Video"}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
        />
      ) : (
        <video
          src={src}
          controls
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        />
      )}
    </div>
  );
}

function CalloutBlock({ block, programColor }) {
  const body = block.text || block.body || "";
  const looksLikeHtml = /<\/?[a-z][\s\S]*?>/i.test(body);
  return (
    <div
      style={{
        background: C.bone,
        borderLeft: `3px solid ${programColor || C.peachDeep}`,
        padding: "18px 22px",
      }}
    >
      {block.title && (
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: programColor || C.peachDeep, textTransform: "uppercase", marginBottom: 8 }}>
          {block.title}
        </div>
      )}
      {looksLikeHtml ? (
        <div className="voya9-richtext" style={{ fontSize: 15, lineHeight: 1.6, color: C.ink }} dangerouslySetInnerHTML={{ __html: body }} />
      ) : (
        <div style={{ fontSize: 15, lineHeight: 1.6, color: C.ink }}>{body}</div>
      )}
    </div>
  );
}

function StepsBlock({ block, programColor }) {
  const steps = block.steps || block.items || [];
  return (
    <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
      {steps.map((s, i) => {
        const body = typeof s === "string" ? s : (s.text || s.body || "");
        const looksLikeHtml = typeof body === "string" && /<\/?[a-z][\s\S]*?>/i.test(body);
        return (
          <li key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <span
              style={{
                minWidth: 32,
                height: 32,
                background: programColor || C.peachDeep,
                color: C.ink,
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
                fontSize: 18,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {i + 1}
            </span>
            <div style={{ flex: 1, fontSize: 15, lineHeight: 1.6, color: C.ink, minWidth: 0 }}>
              {typeof s !== "string" && s.title && (
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{s.title}</div>
              )}
              {looksLikeHtml ? (
                <div className="voya9-richtext" dangerouslySetInnerHTML={{ __html: body }} />
              ) : (
                <div>{body}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Block({ block, programColor }) {
  if (!block || !block.type) return null;
  switch (block.type) {
    case "richtext": return <RichTextBlock block={block} />;
    case "image":    return <ImageBlock block={block} />;
    case "video":    return <VideoBlock block={block} />;
    case "callout":  return <CalloutBlock block={block} programColor={programColor} />;
    case "steps":    return <StepsBlock block={block} programColor={programColor} />;
    default:         return null;
  }
}

export default function ContentTabs({ tabs, programColor, testIdPrefix = "content" }) {
  const safeTabs = Array.isArray(tabs) ? tabs.filter((t) => t && (t.blocks || []).length > 0) : [];
  const [active, setActive] = useState(0);
  const stripRef = useRef(null);
  const tabRefs = useRef([]);

  // When the active tab changes, slide it into the leftmost visible
  // position. On mobile (where the tab strip overflows) this reveals the
  // tabs that were previously off-screen to the right, so members realize
  // there's more content to flip through. On desktop the strip fits its
  // container — scrollLeft caps at 0 and this is effectively a no-op.
  useEffect(() => {
    const strip = stripRef.current;
    const btn = tabRefs.current[active];
    if (!strip || !btn) return;
    // offsetLeft is relative to the nearest positioned ancestor — the
    // strip itself is the offsetParent of its children (it's a flex
    // container with no transform), so this reads as the button's
    // distance from the strip's left edge.
    const target = btn.offsetLeft;
    if (typeof strip.scrollTo === "function") {
      strip.scrollTo({ left: target, behavior: "smooth" });
    } else {
      strip.scrollLeft = target;
    }
  }, [active]);

  if (safeTabs.length === 0) return null;
  const current = safeTabs[active] || safeTabs[0];

  return (
    <div data-testid={`${testIdPrefix}-tabs`}>
      {/* Tab bar — horizontally scrollable on narrow screens.
          The active tab uses box-shadow to paint its peach underline directly
          on top of the container divider (bulletproof — box-shadow doesn't
          interact with margins and always sits at the exact bottom edge).
          overflow-y: hidden + overscroll-behavior: contain prevents the
          finger-scroll vertical wobble users were reporting on iOS. */}
      <div
        ref={stripRef}
        className="no-scrollbar"
        style={{
          display: "flex",
          gap: 0,
          overflowX: "auto",
          overflowY: "hidden",
          overscrollBehavior: "contain",
          borderBottom: `2px solid ${C.warmGray3}`,
          marginBottom: 28,
          position: "relative",
          WebkitOverflowScrolling: "touch",
          // Smooth horizontal snap when the user clicks a tab — the
          // useEffect drives scrollTo, this CSS is a belt for browsers
          // that ignore the smooth option.
          scrollBehavior: "smooth",
        }}
      >
        {safeTabs.map((t, i) => {
          const on = i === active;
          return (
            <button
              key={t.key || t.label || i}
              ref={(el) => { tabRefs.current[i] = el; }}
              type="button"
              data-testid={`${testIdPrefix}-tab-${i}`}
              onClick={() => setActive(i)}
              style={{
                flexShrink: 0,
                position: "relative",
                zIndex: 1,
                padding: "14px 22px",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: on ? C.ink : C.warmGray,
                background: "transparent",
                border: 0,
                whiteSpace: "nowrap",
                cursor: "pointer",
                // Box-shadow draws the active underline at the exact bottom
                // edge regardless of parent border/margin — eliminates the
                // "floating above the divider" artifact on iOS.
                boxShadow: on ? `inset 0 -2px 0 ${programColor || C.peachDeep}` : "none",
              }}
            >
              {t.label || t.key || `Tab ${i + 1}`}
            </button>
          );
        })}
      </div>

      {/* Block stream */}
      <div data-testid={`${testIdPrefix}-panel`} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {(current.blocks || []).map((b, i) => (
          <Block key={i} block={b} programColor={programColor} />
        ))}
      </div>
    </div>
  );
}
